// Shared layout for a Plant-Engineering sub-discipline deep-dive page.
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Calendar, Check, ChevronRight, ExternalLink } from "lucide-react";

export default function PlantSubServicePage({
  slug,
  title,
  eyebrow = "Plant engineering",
  lead,
  intro,                 // array of paragraph strings
  industries,            // string[]
  tools,                 // string[]
  bullets,               // optional array of { title, text? } for richer pages
  heroImage,
  gallery = [],          // string[] image URLs
  thantrickUrl,          // original WP url
  related = [],          // [{ to, label }]
}) {
  useEffect(() => {
    document.title = `${title} — ZeroLabs Digitals`;
  }, [title]);

  return (
    <div data-testid={`page-${slug}`}>
      <section className="pt-28 pb-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(900px_400px_at_50%_-10%,rgba(0,229,255,0.10),transparent_60%)] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
          <Link to="/plant-engineering" className="text-sm text-[#94A3B8] hover:text-[#22D3EE] inline-flex items-center gap-2 mb-6">
            <ArrowLeft size={14}/> Back to Plant Engineering
          </Link>
          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-7">
              <div className="eyebrow">{eyebrow}</div>
              <h1 className="mt-3 text-4xl md:text-6xl font-extrabold tracking-tighter text-white leading-[1.05]">{title}</h1>
              <p className="mt-3 text-xs uppercase tracking-[0.25em] text-[#64748B]">Home / Services / Plant Engineering / {title}</p>
            </div>
            <div className="lg:col-span-5">
              <p className="text-[#94A3B8] leading-relaxed">{lead}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Hero image + intro */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <div className="relative rounded-2xl overflow-hidden border border-white/10">
              <img src={heroImage} alt={title} className="w-full h-[380px] object-cover" loading="lazy"/>
              <div className="absolute inset-0 bg-gradient-to-t from-[#08060F] via-transparent to-transparent"/>
            </div>
          </div>
          <div className="lg:col-span-6">
            {intro.map((p, i) => (
              <p key={i} className="text-[#E2E8F0] leading-relaxed mb-4 text-[15px]">{p}</p>
            ))}
            {thantrickUrl && (
              <a href={thantrickUrl} target="_blank" rel="noreferrer" className="text-sm text-[#22D3EE] hover:text-[#3B82F6] inline-flex items-center gap-1">
                View original on thantrick.org <ExternalLink size={12}/>
              </a>
            )}
          </div>
        </div>
      </section>

      {/* Industries */}
      {industries && industries.length > 0 && (
        <section className="py-8 border-y border-white/5 bg-[#0E0B17]/60">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="text-xs uppercase tracking-[0.25em] text-[#64748B] mb-3">Industries we serve</div>
            <div className="flex flex-wrap gap-2">
              {industries.map((i) => (
                <span key={i} className="px-3 py-1.5 rounded-full text-sm border border-white/10 text-[#E2E8F0] bg-white/[0.03]">{i}</span>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Optional rich bullet list (used by Materials page) */}
      {bullets && bullets.length > 0 && (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <h2 className="text-2xl md:text-3xl font-bold text-white tracking-tight">{bullets[0].sectionTitle || "What we cover"}</h2>
            <div className="mt-8 grid md:grid-cols-2 gap-4">
              {bullets.map((b, i) => (
                <div key={i} className="card p-5">
                  <div className="flex items-start gap-3">
                    <span className="font-mono text-xs text-[#3B82F6] mt-1">{String(i + 1).padStart(2, "0")}</span>
                    <div>
                      <h4 className="text-white font-semibold">{b.title}</h4>
                      {b.items && (
                        <ul className="mt-2 space-y-1.5">
                          {b.items.map((it) => (
                            <li key={it} className="text-sm text-[#94A3B8] flex items-start gap-2">
                              <Check size={13} className="text-[#22D3EE] mt-1 shrink-0"/>{it}
                            </li>
                          ))}
                        </ul>
                      )}
                      {b.text && <p className="text-sm text-[#94A3B8] mt-2 leading-relaxed">{b.text}</p>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Toolchain */}
      {tools && tools.length > 0 && (
        <section className="py-12">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="eyebrow">Toolchain</div>
            <h2 className="mt-2 text-2xl md:text-3xl font-bold text-white tracking-tight">Software we ship with</h2>
            <div className="mt-6 flex flex-wrap gap-2.5">
              {tools.map((t) => (
                <span key={t} className="px-4 py-2 rounded-full text-sm border border-white/15 text-white bg-white/[0.04] hover:border-[#22D3EE]/40 transition-colors">{t}</span>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Gallery */}
      {gallery.length > 0 && (
        <section className="py-12">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {gallery.map((src, i) => (
                <div key={i} className="rounded-xl overflow-hidden border border-white/10 bg-black/30">
                  <img src={src} alt="" loading="lazy" className="w-full h-56 object-cover"/>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Related */}
      {related.length > 0 && (
        <section className="py-12 border-t border-white/5">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="eyebrow">Related disciplines</div>
            <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {related.map((r) => (
                <Link key={r.to} to={r.to} className="card p-4 flex items-center justify-between hover:border-[#22D3EE]/40">
                  <span className="text-white text-sm font-medium">{r.label}</span>
                  <ChevronRight size={16} className="text-[#22D3EE]"/>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-20 border-t border-white/5">
        <div className="max-w-5xl mx-auto px-6 md:px-10 text-center">
          <div className="eyebrow">Let's build together</div>
          <h2 className="mt-3 text-3xl md:text-5xl font-bold text-white tracking-tight">Have a {title.toLowerCase()} project?</h2>
          <p className="mt-4 text-[#94A3B8] max-w-2xl mx-auto">
            Tell us your scope — we'll come back within one business day with a fit, a plan and an estimate.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <a href="/#book" className="btn-primary"><Calendar size={16}/> Book a consultation</a>
            <a href="/contact" className="btn-secondary">Send a brief <ArrowRight size={14}/></a>
          </div>
        </div>
      </section>
    </div>
  );
}
